import React, { useRef, useState } from 'react'
import QRCode from 'qrcode.react'
import { useTranslation } from 'react-i18next'
import Slider from '@mui/material/Slider'
import { toast } from 'react-toastify'
import ReactDOMServer from 'react-dom/server'
import { toPng } from 'html-to-image'
import { copyActive } from '../../images'

interface QRCodeData {
  qrData: {
    short_url: string
    qr_code: { color: string; logo: string }
  }
}

const QRCodeDownload = ({ qrData }: QRCodeData) => {
  const { t } = useTranslation()
  const [imageFormat, setImageFormat] = useState('png')
  const [transparentBackground, setTransparentBackground] = useState(false)
  const [resolution, setResolution] = useState(75)
  const qrSelected = {
    short_url: qrData.short_url,
    color: qrData.qr_code.color,
    logo: qrData.qr_code.logo,
  }


  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setResolution(newValue as number)
  }

  const copyLink = (short_url: string) => {
    navigator.clipboard.writeText(short_url)
    toast(`${t('LINK_COPIED')}`)
  }

  const handleDownloadQrCode = async () => {
    if (imageFormat === '') {
      toast(t('SELECT_FORMAT'))
      return
    }

    if (imageFormat === 'svg' && qrSelected) {
      const qrCodeSvg = ReactDOMServer.renderToString(
        <QRCode
          renderAs='svg'
          value={qrSelected.short_url}
          fgColor={qrSelected.color}
          size={resolution}
        />
      )

      const combinedSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="${resolution}" height="${resolution}">
      ${qrCodeSvg}</svg> `

      const blob = new Blob([combinedSvg], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `qrcode.${imageFormat}`
      link.click()
      URL.revokeObjectURL(url)
    } else {
      const qrCodeContainer = document.getElementById('qr-code')

      if (qrCodeContainer) {
        toPng(qrCodeContainer, {
          backgroundColor: transparentBackground ? 'transparent' : 'white',
          height: resolution,
          width: resolution,
        })
          .then((dataUrl) => {
            const link = document.createElement('a')
            link.download = `qrcode.${imageFormat}`
            link.href = dataUrl
            link.click()
          })
          .catch(function (error) {
            console.error(error)
          })
      }
    }
  }

  return (
    <div className='d-flex flex-column div-body-padding'>
      <div className='d-flex flex-column div-body-padding'>
        <div className='container-qr-code'>
          <QRCode
            id='qr-code'
            value={qrSelected.short_url}
            fgColor={qrSelected.color}
            bgColor='transparent'
            level='H'
            size={100}
            imageSettings={{
              src: qrSelected.logo,
              height: 24,
              width: 24,
              excavate: true,
            }}
          />
        </div>

        <span className='small padding-bottom-1'>{`${t(
          'QR_CODE_CHARACTERISTICS'
        )}`}</span>
        <div>
          <span className='small padding-right-1'>{qrSelected.short_url}</span>
          <img
            className='button-card-icon'
            src={copyActive}
            alt={`${t('COPY')}`}
            onClick={() => {
              copyLink(qrSelected.short_url)
            }}
          />
        </div>
      </div>

      <div className='container-buttton-images-format'>
        <button
          className={`${
            imageFormat === 'png' ? 'button-first' : 'button-fourth'
          } width-90`}
          type='button'
          onClick={() => setImageFormat('png')}
        >
          {`${t('.PNG')}`}
        </button>
        <button
          className={`${
            imageFormat === 'jpg' ? 'button-first' : 'button-fourth'
          } width-90 mx-4`}
          type='submit'
          onClick={() => setImageFormat('jpg')}
        >
          {`${t('.JPG')}`}
        </button>
        <button
          className={`${
            imageFormat === 'svg' ? 'button-first' : 'button-fourth'
          } width-90`}
          type='button'
          onClick={() => setImageFormat('svg')}
        >
          {`${t('.SVG')}`}
        </button>
      </div>

      <div>
        <Slider
          size='medium'
          defaultValue={100}
          aria-label='Small'
          valueLabelDisplay='auto'
          min={50}
          step={10}
          max={500}
          className='color-primary'
          value={resolution}
          onChange={handleSliderChange}
        />
        <div className='container-slider-legend'>
          <span className='small'>{`${t('SMALL_SIZE')}`}</span>
          <span className='resolution-title title mx-4'>{`${resolution} X ${resolution}`}</span>
          <span className='small'>{`${t('BIG_SIZE')}`}</span>
        </div>
      </div>

      <div className='container-check-image-format'>
        <input
          id='transparentBackground'
          className='input-checkbox-general'
          defaultChecked={false}
          type='checkbox'
          checked={transparentBackground}
          onChange={() => setTransparentBackground(!transparentBackground)}
        />

        <label
          htmlFor='transparentBackground'
          className='label-input-checkbox-custom'
          dangerouslySetInnerHTML={{
            __html: `${t('TRANSPARENT_BACKGROUND')}`,
          }}
        />
      </div>
      <div className='container-button'>
        <button
          className='button-get-started-secondary width-auto'
          type='submit'
          onClick={handleDownloadQrCode}
        >
          {`${t('DOWNLOAD_QR_CODE')}`}
        </button>
      </div>
    </div>
  )
}

export default QRCodeDownload
